import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import { Bug, Lightbulb, Clock, CheckCircle, AlertCircle, MessageSquare, ChevronDown, ChevronUp, Send, X, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageSupportRequests = () => {
  const { isDark } = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [addingToBacklog, setAddingToBacklog] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/support-requests`;
      const params = new URLSearchParams();
      if (filterType) params.append('request_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/support-requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchRequests();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const addNote = async (requestId) => {
    if (!newNote.trim()) return;
    setSubmittingNote(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/support-requests/${requestId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newNote })
      });
      if (response.ok) {
        setNewNote('');
        fetchRequests();
      }
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setSubmittingNote(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[severity] || styles.medium}`}>
        {severity?.toUpperCase()}
      </span>
    );
  };

  const getSeverityBall = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return (
      <span className={`inline-block w-3 h-3 rounded-full ${colors[severity] || colors.medium}`} title={severity?.toUpperCase()} />
    );
  };

  const addToBacklog = async (request) => {
    setAddingToBacklog(request.request_id);
    
    // Map severity to priority
    const priorityMap = {
      critical: 'critical',
      high: 'high',
      medium: 'medium',
      low: 'low'
    };
    
    // Map request type to category
    const categoryMap = {
      bug: 'Feature',
      feature: 'Feature'
    };

    const backlogItem = {
      title: request.title,
      description: `${request.description}\n\n---\nOriginal request from: ${request.reporter_name} (${request.reporter_email})\nRequest ID: ${request.request_id}`,
      priority: priorityMap[request.severity] || 'backlog',
      category: categoryMap[request.request_type] || 'Other',
      story_points: null
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/backlog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(backlogItem)
      });

      if (response.ok) {
        alert('Added to product backlog successfully!');
        // Optionally update the request status
        await updateStatus(request.request_id, 'in_progress');
      } else {
        alert('Failed to add to backlog');
      }
    } catch (error) {
      console.error('Failed to add to backlog:', error);
      alert('Failed to add to backlog');
    } finally {
      setAddingToBacklog(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-purple-500/20 text-purple-400',
      resolved: 'bg-green-500/20 text-green-400',
      closed: 'bg-gray-500/20 text-gray-400'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${styles[status] || styles.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    return type === 'bug' ? (
      <Bug className="w-4 h-4 text-red-400" />
    ) : (
      <Lightbulb className="w-4 h-4 text-yellow-400" />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectClass = `${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 text-sm focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00]`;

  if (loading) {
    return (
      <AppLayout title="Manage Support Requests">
        <div className="text-[#A2FF00]">Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Manage Support Requests">
      {/* Filters */}
      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 mb-6`}>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={selectClass}
            >
              <option value="">All Types</option>
              <option value="bug">Bug Reports</option>
              <option value="feature">Feature Requests</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={selectClass}
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-gray-400 text-sm">
              Showing {requests.length} request{requests.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 text-center`}>
          <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No support requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.request_id}
              className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}
            >
              {/* Request Header */}
              <div
                className={`p-4 cursor-pointer ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}
                onClick={() => setExpandedRequest(expandedRequest === request.request_id ? null : request.request_id)}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 flex items-center gap-2">
                    {getSeverityBall(request.severity)}
                    {getTypeIcon(request.request_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getSeverityBadge(request.severity)}
                      {getStatusBadge(request.status)}
                      <span className="text-gray-500 text-xs">
                        #{request.request_id?.split('_')[1]}
                      </span>
                    </div>
                    <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium truncate`}>{request.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <span>{request.reporter_name}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(request.created_at)}
                      </span>
                      {request.admin_notes?.length > 0 && (
                        <>
                          <span>•</span>
                          <span className={`flex items-center gap-1 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                            <MessageSquare className="w-3 h-3" />
                            {request.admin_notes.length} note{request.admin_notes.length !== 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToBacklog(request);
                      }}
                      disabled={addingToBacklog === request.request_id}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm flex items-center gap-1 ${
                        isDark 
                          ? 'bg-[#A2FF00]/20 text-[#A2FF00] hover:bg-[#A2FF00]/30' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      } transition-colors disabled:opacity-50`}
                      title="Add to Product Backlog"
                    >
                      <Plus className="w-3 h-3" />
                      {addingToBacklog === request.request_id ? 'Adding...' : 'Backlog'}
                    </button>
                    {expandedRequest === request.request_id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedRequest === request.request_id && (
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} p-4`}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Details */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Description</label>
                        <p className={`${isDark ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap`}>{request.description}</p>
                      </div>

                      {request.request_type === 'bug' && (
                        <>
                          {request.steps_to_reproduce && (
                            <div>
                              <label className="block text-gray-400 text-sm mb-1">Steps to Reproduce</label>
                              <p className={`${isDark ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap`}>{request.steps_to_reproduce}</p>
                            </div>
                          )}
                          {request.expected_behavior && (
                            <div>
                              <label className="block text-gray-400 text-sm mb-1">Expected Behavior</label>
                              <p className={`${isDark ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap`}>{request.expected_behavior}</p>
                            </div>
                          )}
                          {request.actual_behavior && (
                            <div>
                              <label className="block text-gray-400 text-sm mb-1">Actual Behavior</label>
                              <p className={`${isDark ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap`}>{request.actual_behavior}</p>
                            </div>
                          )}
                        </>
                      )}

                      {request.attachment_url && (
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">Attachment</label>
                          <a
                            href={`${BACKEND_URL}${request.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${isDark ? 'text-[#A2FF00] hover:underline' : 'text-green-600 hover:underline'} text-sm`}
                          >
                            View Attachment
                          </a>
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <label className="text-gray-400 text-sm">Status:</label>
                        <select
                          value={request.status}
                          onChange={(e) => updateStatus(request.request_id, e.target.value)}
                          className={selectClass}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>

                      <div className={`pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <div className="text-gray-500 text-sm">
                          <p>Reporter: {request.reporter_name} ({request.reporter_email})</p>
                          <p>Created: {formatDate(request.created_at)}</p>
                          <p>Last Updated: {formatDate(request.updated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Admin Notes */}
                    <div className={`lg:border-l ${isDark ? 'lg:border-white/10' : 'lg:border-gray-200'} lg:pl-6`}>
                      <h4 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-4 flex items-center gap-2`}>
                        <MessageSquare className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                        Admin Notes
                      </h4>

                      {/* Existing Notes */}
                      <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                        {request.admin_notes?.length === 0 ? (
                          <p className="text-gray-500 text-sm">No notes yet</p>
                        ) : (
                          request.admin_notes?.map((note) => (
                            <div key={note.note_id} className={`${isDark ? 'bg-black/50 border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-3`}>
                              <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm whitespace-pre-wrap`}>{note.content}</p>
                              <div className="mt-2 text-xs text-gray-500">
                                {note.created_by_name} • {formatDate(note.created_at)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Add Note */}
                      <div className="flex gap-2">
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Add a note..."
                          rows={2}
                          className={`flex-1 ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-3 py-2 text-sm focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none resize-none`}
                        />
                        <button
                          onClick={() => addNote(request.request_id)}
                          disabled={submittingNote || !newNote.trim()}
                          className={`px-4 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
};

export default ManageSupportRequests;
